import { type FunctionComponent } from "react";
import { Alert, Form, InputNumber, Radio, Switch } from "antd";
import { type Dayjs } from "dayjs";

import {
  followUpFitsInOffer,
  MAX_BONUS_DAYS,
  MAX_FOLLOW_UP_DAYS,
  MIN_BONUS_DAYS,
  MIN_FOLLOW_UP_DAYS,
  type PayoffOfferCampaignModel,
} from "./payoffOfferCampaign";

interface OwnProps {
  /** Live form values — the fields depend on each other. */
  values: Partial<PayoffOfferCampaignModel>;
}

/**
 * Reward and reminder — the part of the grant form TRE-1721 added, shared by the
 * single-client and the bulk modal so the two cannot drift apart.
 *
 * `w-full` on the alert is not cosmetic — the bulk modal renders these in an
 * inline (flex-wrap) form, where a narrow alert would squeeze in next to a field.
 */
export const PayoffOfferCampaignFields: FunctionComponent<OwnProps> = ({ values }) => (
  <>
    <Form.Item
      label="Nagroda"
      name="rewardType"
      tooltip="Rabat to stały procent od pozostałego salda. „Ostatnia rata gratis” liczy kwotę osobno dla każdej klientki z jej harmonogramu. „Dodatkowe dni” nie zmienia ceny — klientka płaci pełną kwotę pozostałych rat i dostaje dłuższy dostęp."
    >
      <Radio.Group
        optionType="button"
        options={[
          { label: "Rabat %", value: "discount" },
          { label: "Ostatnia rata gratis", value: "lastInstallmentFree" },
          { label: "Dodatkowe dni dostępu", value: "bonusTime" },
        ]}
      />
    </Form.Item>

    {values.rewardType === "lastInstallmentFree" && (
      <Alert
        type="info"
        showIcon
        className="mb-4 w-full"
        message="Kwota liczona per klientka"
        description="Każda płaci sumę pozostałych rat pomniejszoną o jedną — niezależnie od tego, ile rat jej zostało. Mail mówi „jedną ratę masz gratis” i podaje jej własną kwotę, więc jedna wysyłka jest prawdziwa dla wszystkich. Dostęp do programu się nie zmienia."
      />
    )}

    {values.rewardType === "bonusTime" && (
      <>
        <Form.Item
          label="Dodatkowe dni dostępu"
          name="bonusDays"
          rules={[{ required: true, message: "Podaj liczbę dni" }]}
        >
          <InputNumber min={MIN_BONUS_DAYS} max={MAX_BONUS_DAYS} precision={0} addonAfter="dni" />
        </Form.Item>
        <Alert
          type="info"
          showIcon
          className="mb-4 w-full"
          message="Pełna cena, dłuższy dostęp"
          description="Klientka płaci całą pozostałą kwotę — bez rabatu i bez darmowej raty — a jej dostęp przesuwa się o tyle dni. Jeżeli ma podpięty kolejny pakiet, jego start przesuwa się o tyle samo, więc darmowe dni nie nakładają się na okres, za który i tak płaci."
        />
      </>
    )}

    <Form.Item
      label="Przypomnienie, jeśli nie skorzysta"
      name="followUpEnabled"
      valuePropName="checked"
      tooltip="Jeden mail przypominający. Nie wyjdzie do klientki, która w międzyczasie dopłaciła, ani po wygaśnięciu oferty."
    >
      <Switch disabled={!values.sendEmail} />
    </Form.Item>
    {/* Rendered only when the reminder is on. A greyed-out field with nothing to
        set is noise — and in the bulk modal it lands on a row of its own. The
        value survives unmounting (antd preserves it), so toggling back and
        forth does not lose what the operator typed. */}
    {Boolean(values.sendEmail && values.followUpEnabled) && (
      <Form.Item
        label="Po ilu dniach"
        name="followUpAfterDays"
        dependencies={["expiresAt", "followUpEnabled"]}
        rules={[
          {
            validator: (_, value: number) => {
              if (!followUpFitsInOffer(value, values.expiresAt as Dayjs | undefined)) {
                return Promise.reject(new Error("Przypomnienie wypadłoby po wygaśnięciu oferty"));
              }

              return Promise.resolve();
            },
          },
        ]}
      >
        <InputNumber min={MIN_FOLLOW_UP_DAYS} max={MAX_FOLLOW_UP_DAYS} precision={0} addonAfter="dni" />
      </Form.Item>
    )}
  </>
);
